<template>
    <div class="create-or-view-change-shareholder-nominator"> 
        <b-sidebar
            id="sidebar-task-handler"
            data-test="change-nominee-shareholder-sidebar"
            sidebar-class="sidebar-lg"
            :visible="isGenerateDocumentSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="(val) => $emit('update:is-generate-document-sidebar-active', val)"
            @hidden="() => clearFormData()"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1 side-bar-header">
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <validation-observer ref="refChangeNomineeShareholderForm">
                    <div class="px-1 mt-1">
                        <!-- form body -->
                        <validation-provider
                            v-slot="{ errors }"
                            name="nominee shareholder name"
                            rules="required"
                        >
                            <b-form-group label="Nominee shareholder name" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nominee-shareholder-name"
                                    ref="nomineeShareholder"
                                    v-model="changeNominatorLocal.nomineeShareholder"
                                    class="custom-select-icon"
                                    placeholder="Select nominee shareholder"
                                    label="name" 
                                    :clearable="false"
                                    :disabled = "isDisabled || dataStatus === 'edit'"
                                    :options="nomineeShareholderOptions"
                                    @input="onNomineeShareholderChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <!-- nominator fields-->
                        <div class="nominator-data-fields">
                            <!-- nominator new or existing -->
                            <div v-if="shouldShowExistingOrNewNominatorButton" class="d-flex align-items-center mt-1">
                                <b-button
                                    variant="outline-primary"
                                    class="btn btn-sm d-flex justify-content-end align-items-center mb-1"
                                    style="font-size: 1em;"
                                    data-test="add-existing-nominator-btn"
                                    :disabled="changeNominatorLocal.isExistingNominator"
                                    @click="changeNominatorLocal.isExistingNominator = true"
                                >
                                    Existing Nominator
                                    <vector
                                        data-name="add-user"
                                        data-width="15px"
                                        data-height="12px"
                                        class="ml-.3 d-flex justify-content-center align-items-center"
                                    />
                                </b-button>

                                <b-button
                                    variant="outline-primary"
                                    class="btn btn-sm d-flex justify-content-end align-items-center mb-1 ml-1"
                                    style="font-size: 1em;"
                                    data-test="add-new-nominator-btn"
                                    :disabled="! changeNominatorLocal.isExistingNominator"
                                    @click="changeNominatorLocal.isExistingNominator = false"
                                >
                                    New Nominator
                                    <vector
                                        data-name="add-user"
                                        data-width="15px"
                                        data-height="12px"
                                        class=" ml-.3 d-flex justify-content-center align-items-center"
                                    />
                                </b-button>
                            </div>
                            <!-- end nominator new or existing -->
                        </div>

                        <validation-provider
                            #default="{ errors }"
                            name="nominator type"
                            rules="required"
                            class="end-align-indicator-long-select"
                        >
                            <b-form-group
                                label="Nominator type"
                                :state="errors.length > 0 ? false : null"
                            >
                                <v-select
                                    ref="nominatorType"
                                    v-model="changeNominatorLocal.nominatorType"
                                    class="custom-select-icon text-capitalize"
                                    :clearable="false"
                                    placeholder="Select nominator type"
                                    label="text"
                                    :options="nominatorTypeOptions"
                                    :disabled="isDisabled"
                                    @input="onNominatorTypeChange"
                                />
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="isFetching" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <!-- existing nominator -->
                        <div v-if="changeNominatorLocal.isExistingNominator">
                            <validation-provider
                                v-slot="{ errors }"
                                name="nominator"
                                rules="required"
                                class="end-align-indicator-long-select"
                            >
                                <b-form-group label="Nominator" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        id="nominator"
                                        ref="nominator"
                                        v-model="changeNominatorLocal.nominator"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        placeholder="Select"
                                        label="name"
                                        :state="errors.length > 0 ? false : null"
                                        :options="nominatorsOptions"
                                        :disabled="isDisabled"
                                        @input="onNominatorChange"
                                    >
                                        <template #option="data">
                                            <div class="text-wrap text-capitalize"> {{ data.name }} </div> 
                                        </template>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- new nominator -->
                        <div v-else>
                            <validation-provider
                                v-slot="{ errors }"
                                :name="dynamicIdLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group>
                                    <slot name="label">
                                        <label id="nominator-id" for="nominator-id">
                                            {{ dynamicIdLabel }}
                                        </label>
                                    </slot>

                                    <b-form-input
                                        id="nominator-id"
                                        v-model="changeNominatorLocal.nominatorNRIC"
                                        :state="errors.length > 0 ? false : null"
                                        :placeholder="dynamicIdPlaceholder"
                                        :disabled="isDisabled"
                                        trim
                                        data-test ="nominator-nric"
                                        @input="requestAutoFillNominatorFormData"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <div v-if="isNominatorTypeIndividual">
                                <validation-provider
                                    v-slot="{ errors }"
                                    name="id type"
                                    rules="required"
                                    class="end-align-indicator-long-select"
                                >
                                    <b-form-group label="ID type" :state="errors.length > 0 ? false : null">
                                        <v-select
                                            ref="nominatorIdType"
                                            v-model="changeNominatorLocal.nominatorIdType"
                                            class="custom-select-icon"
                                            :clearable="false"
                                            placeholder="Select ID type"
                                            label="name"
                                            :options="idTypeOptions"
                                            :disabled="isDisabled"
                                            @input="(idType) => handleIdTypeOnChange(idType, 'nominatorNationality')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>

                                <validation-provider
                                    v-slot="{ errors }"
                                    name="nationality"
                                    rules="required"
                                    class="end-align-indicator-long-select"
                                >
                                    <b-form-group label="Nationality" :state="errors.length > 0 ? false : null" >
                                        <v-select
                                            id="nominator-nationality"
                                            ref="nominatorNationality"
                                            v-model="changeNominatorLocal.nominatorNationality"
                                            class="custom-select-icon"
                                            label="text" 
                                            placeholder="Select nationality"
                                            :clearable="false"
                                            :options="nationalityOptions"
                                            :disabled ="isDisabled || shouldDisabledNewNominatorNationality"
                                        >
                                        </v-select>
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>

                                <validation-provider
                                    v-if="shouldShowNominatorExpiryDate"
                                    v-slot="{ errors }"
                                    name="nominator date of expiry"
                                    rules="required"
                                >
                                    <b-form-group :state="errors.length > 0 ? false : null">
                                        <slot name="label">
                                            <label id="dateOfExpiry" for="id">
                                                Date of expiry
                                                <vector
                                                    data-type="icon"
                                                    data-width="15px"
                                                    data-height="15px"
                                                    data-class="mr-.5"
                                                    data-name="information"
                                                ></vector>
                                            </label>
                                        </slot>

                                        <b-popover
                                            target="dateOfExpiry"
                                            triggers="hover"
                                            placement="right"
                                            variant="black"
                                        >
                                            Skip this if you are a Singapore citizen or permanent
                                            resident.
                                            <br />
                                            Fill in the date of expiry of your passport or FIN if
                                            you are a foreigner.
                                        </b-popover>

                                        <flat-pickr
                                            v-model="changeNominatorLocal.nominatorDateOfExpiry"
                                            placeholder="Enter date of expiry"
                                            class="form-control"
                                            :config="datePickerConfig"
                                            :disabled = "isDisabled"
                                            data-test="date-of-expiry"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>
                            </div>

                            <validation-provider 
                                v-slot="{ errors }"
                                :name="dynamicNewNominatorNameLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group :label="dynamicNewNominatorNameLabel">
                                    <b-form-input
                                        id="nominator-name"
                                        v-model="changeNominatorLocal.nominatorName"
                                        :placeholder="dynamicNewNominatorPlaceholder"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="nominator-name"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <div v-if="isNominatorTypeIndividual">
                                <validation-provider
                                    v-slot="{ errors }"
                                    name="date of birth"
                                    rules="required"
                                >
                                    <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                                        <flat-pickr
                                            v-model="changeNominatorLocal.nominatorDOB"
                                            class="form-control"
                                            data-test="date-of-birth"
                                            placeholder="Enter date of birth"
                                            :config="datePickerConfig"
                                            :state="errors.length > 0 ? false : null"
                                            :disabled="isDisabled"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>
                            </div>

                            <validation-provider
                                v-slot="{ errors }"
                                :name="dynamicAddressLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group :label="dynamicAddressLabel">
                                    <b-form-textarea
                                        id="nominator-address"
                                        v-model="changeNominatorLocal.nominatorAddress"
                                        :placeholder="dynamicAddressPlaceholder"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        trim
                                        rows="3"
                                        max-rows="3"
                                        data-test="nominator-address"
                                    >
                                    </b-form-textarea>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- < CORPORATE NOMINATOR > -->
                        <div v-if="! isNominatorTypeIndividual">
                            <validation-provider 
                                #default="{ errors }"
                                name="corporate nominator UEN"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator UEN issued by the registrar (ACRA)">
                                    <b-form-input
                                        id="name"
                                        v-model="changeNominatorLocal.nominatorUen"
                                        placeholder="Enter corporate nominator UEN"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="nominator-uen"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                #default="{ errors }"
                                name="corporate nominator legal form"
                                rules="required"
                                class="end-align-indicator-long-select"
                            >
                                <b-form-group
                                    label="Corporate nominator legal form"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <v-select
                                        ref="legalForm"
                                        v-model="changeNominatorLocal.nominatorLegalForm"
                                        placeholder="Select corporate nominator legal form"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="text"
                                        :options="legalForms"
                                        :disabled="isDisabled"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                #default="{ errors }"
                                name="corporate nominator jurisdiction"
                                rules="required"
                                class="end-align-indicator-long-select"
                            >
                                <b-form-group label="Corporate nominator jurisdiction" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        ref ="jurisdiction"
                                        v-model="changeNominatorLocal.nominatorJurisdiction"
                                        :clearable="false"
                                        :options="jurisdictions"
                                        :disabled="isDisabled"
                                        label="name"
                                        value="id"
                                        class="custom-select-icon text-capitalize"
                                        placeholder="Select corporate nominator jurisdiction"
                                        @input="onNominatorJurisdictionChange"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider #default="{ errors }"
                                name="corporate nominator statue"
                                rules="required"
                                mode="lazy"
                                class="end-align-indicator-long-select"
                            >
                                <b-form-group label="Corporate nominator statue under which the corporate is formed" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        ref="statue"
                                        v-model="changeNominatorLocal.nominatorStatue"
                                        placeholder="Select corporate nominator statue"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="name"
                                        value="id"
                                        :options="statues"
                                        :disabled="isDisabled"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider #default="{ errors }"
                                name="registrar"
                                rules="required"
                                mode="lazy"
                                class="end-align-indicator-long-select"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null" label="Name of corporate nominator's register of the jurisdiction where the legal entity is formed or incorporated">
                                    <v-select
                                        ref="nominatorRegistrar"
                                        v-model="changeNominatorLocal.nominatorRegistrar"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="name"
                                        value="id"
                                        :options="registrars"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        placeholder="Select corporate registrar"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>
                        <!-- end corporate nominator fields -->

                        <div v-if="isNominatorTypeIndividual">
                            <validation-provider
                                #default="{ errors }"
                                name="individual nominator aliases"
                            >
                                <b-form-group label="Individual nominator aliases, if any">
                                    <b-form-input
                                        id="name"
                                        v-model="changeNominatorLocal.nominatorAlias"
                                        placeholder="individual nominator aliases"
                                        :state="errors.length > 0 ? false : null"
                                        :disabled="isDisabled"
                                        data-test="nominator-alias"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- form action -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                data-test="create-change-of-fye-form-btn"
                                @click="generateChangeNominatorForm"
                                >
                                <img
                                    v-if="dataStatus === 'prepare-for-generate' && !dataSidebarActionLoading"
                                    :src="'/icons/papers_white.svg'"
                                    class="mr-.5"
                                    alt="add-logo"
                                    width="13px"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button" />

                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    Generate
                                </span>
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-generate-document-sidebar-active', false)"
                                >Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </div>
                </validation-observer> 
            </template>
        </b-sidebar>
    </div>
  </template>
  
<script>
import { BButton, BFormGroup, BFormInput, BFormTextarea, BPopover, BSidebar } from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import { debounce } from 'debounce';
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import { NOMINEE_SHAREHOLDER, NRIC_CITIZEN_ID, SHAREHOLDER, SINGAPORE_CITIZEN } from '@/constants/config';
import User from '@/models/User';
import IdType from "@/models/IdType";
import Nationality from '@/models/Nationality';
import CompanyNomineeShareholder from '@/models/CompanyNomineeShareholder';
import Nominator from '@/models/Nominator';
import { formatDate } from '@/helpers';
  
export default {
    components:{
        BButton,
        BFormGroup,
        BFormInput, 
        BFormTextarea, 
        BPopover, 
        BSidebar,
        vSelect,
        ValidationProvider,
        ValidationObserver,
        flatPickr,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isGenerateDocumentSidebarActive",
        event: "update:is-generate-document-sidebar-active",
    },

    props: {
        isGenerateDocumentSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataToView: {
            type: Object,
            required: true,
        },

        dataStatus: { 
            type: String,
            default: "view"
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            required,
            isFetching: false,
            NO_MATCHING_OPTIONS,
            defaultKey: 0,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },
  
            dataShareholders: [],
            
            nationalityOptions: [],
            idTypeOptions: [],
            nomineeShareholderOptions: [],
            nominatorTypeOptions: [],
            nominatorsOptions: [],
            
            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            nominators: [],
            jurisdictions: [],

            changeNominatorLocal: {
                nomineeShareholder: this.dataToView.nominee_shareholder,
                
                // nominator
                nominatorType: this.dataToView.nominatorType,
                isExistingNominator: this.dataToView.isExistingNominator,

                // existing nominator
                nominator: this.dataToView.nominator,
                
                // new nominator
                nominatorName: this.dataToView.nominatorName,
                nominatorEmail: this.dataToView.nominatorEmail,
                nominatorNRIC: this.dataToView.nominatorNRIC,
                nominatorIdType: this.dataToView.nominatorIdType,
                nominatorNationality: this.dataToView.nominatorNationality,
                nominatorDateOfExpiry: this.dataToView.nominatorDateOfExpiry,
                nominatorDOB: this.dataToView.nominatorDOB,
                nominatorAddress: this.dataToView.nominatorAddress,
                
                nominatorUen: this.dataToView.nominatorUen,
                nominatorLegalForm: this.dataToView.nominatorLegalForm,
                nominatorJurisdiction: this.dataToView.nominatorJurisdiction,
                nominatorStatue: this.dataToView.nominatorStatue,
                nominatorRegistrar: this.dataToView.nominatorRegistrar,

                nominatorAlias: this.dataToView.nominatorAlias,
            },
        };
    },

    computed: {
        isDisabled() {
            return this.dataStatus === 'view';
        },

        sideBarTitle() {
            return "Change Shareholder's Nominator Form";
        },

        isNominatorTypeIndividual() {
            // if there is no type yet, act like it's Individual
            if (! this.changeNominatorLocal.nominatorType) return true;

            return (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL
            );
        },

        shouldDisabledNationality() {
            return this.changeNominatorLocal?.idType?.id === NRIC_CITIZEN_ID;
        },

        shouldDisabledNewNominatorNationality() {
            return this.changeNominatorLocal?.nominatorIdType?.id === NRIC_CITIZEN_ID;
        },

        shouldShowExpiryDate()
        {
            if(this.changeNominatorLocal.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.changeNominatorLocal.dateOfExpiry = null;
            }
            return ! this.changeNominatorLocal.idType?.name.includes("NRIC");
        },

        shouldShowNominatorExpiryDate() {
            if(this.changeNominatorLocal.nominatorIdType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.changeNominatorLocal.nominatorDateOfExpiry = null;
            }
            return ! this.changeNominatorLocal.nominatorIdType?.name.includes("NRIC");
        },

        dynamicIdLabel() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator id on the corporate entity register of the jurisdiction where the corporate controller is formed or incorporated";

            return "Individual nominator ID";
        },

        dynamicIdPlaceholder() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator id";

            return "Enter individual nominator id";
        },

        dynamicNameLabel() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Individual nominator name";
        },

        dynamicNamePlaceholder() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Select corporate nominator";

            return "Select individual nominator";
        },

        shouldShowExistingOrNewNominatorButton() {
            return this.dataStatus !== 'view';
        },

        // new nominator fields
        dynamicNewNominatorNameLabel() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Name";
        },

        dynamicNewNominatorPlaceholder() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator name";

            return "Enter nominator name";
        },

        dynamicAddressLabel() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Address of registered office";

            return "Address";
        },

        dynamicAddressPlaceholder() {
            if (
                this.changeNominatorLocal.nominatorType &&
                this.changeNominatorLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter address of registered office";

            return "Enter address";
        },
    },

    watch: {
        isGenerateDocumentSidebarActive(isActive) {
            if(isActive && this.dataStatus === 'prepare-for-generate') {
                this.changeNominatorLocal.isExistingNominator = true;
                this.getRequiredDataToGenerateChangeNominator();
            }

            if(isActive && this.dataStatus !== 'prepare-for-generate') {
                this.setupLocalData();
            }
        },
    },

    methods: {
        setupLocalData() {
            Object.keys(this.changeNominatorLocal).forEach(key => {
                this.changeNominatorLocal[key] = this.dataToView[key];
            });
        },

        async getRequiredDataToGenerateChangeNominator() {
            this.isFetching = true;

            if(this.dataStatus !== 'view') {
                // nominator options
                const dataNominators = await CompanyNomineeShareholder.getNominatorOptions();
                this.dataShareholders = dataNominators.map(nominator => ({
                    reference_id: nominator.reference_id,
                    user_id: nominator.user.id,
                    nric: nominator.user.user_info.NRIC,
                    shareholderType: { 
                        text: nominator.shareholder_type,
                        value: nominator.shareholder_type,
                    },
                    name: nominator.user?.user_info?.legal_name ?? nominator.user.name,
                    email: nominator.user.email
                }));

                // nationality option
                const data = await Nationality.all();
                this.nationalityOptions = data.map(obj => ({
                    value: obj.id,
                    text: obj.name
                }));

                // id type option
                this.idTypeOptions = await IdType.all();
            
                // get nominator type options
                const nominatorTypeOptionsData = await CompanyNomineeShareholder.getNominatorTypeOptions();
                this.nominatorTypeOptions = nominatorTypeOptionsData.map(opt => {
                    const text  = opt;
                    const value  = opt;
                    return { text, value };
                });

                // if id type is NRIC citizen and nationality value not set, it should be singapore citizen
                this.handleIdTypeOnChange(this.changeNominatorLocal?.idType); 

                this.reInitializeNominators(this.changeNominatorLocal.nominatorType ? this.changeNominatorLocal.nominatorType.text : NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL);

                const payload = await Nominator.getCorporateData();

                this.jurisdictions = payload.jurisdiction;
                this.statues = payload.statue;
                this.dataStatues = this.statues;
                this.legalForms = payload.legal_form;
                this.registrars = payload.registrar;
                this.dataRegistrars = this.registrars;

                // nominee nominee shareholder option
                const activeNomineeShareholders = await CompanyNomineeShareholder.getActiveNomineeShareholders();
                this.nomineeShareholderOptions = activeNomineeShareholders.map(nomineeShareholder => ({
                    company_shareholder_id: nomineeShareholder.company_shareholder_id,
                    user_id: nomineeShareholder.company_shareholder.user.id,
                    name: nomineeShareholder.company_shareholder.user?.user_info?.legal_name ?? nomineeShareholder.company_shareholder.user.name,
                    email: nomineeShareholder.company_shareholder.user.email,
                    originData: nomineeShareholder.shareholder_nominator
                }));
            }
            
            this.isFetching = false;
        },

        handleIdTypeOnChange(data, nationalityKey = 'nationality')
        {
            if(data && data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationalityOptions.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                if(nationalityKey === 'nominatorNationality')
                    this.changeNominatorLocal[nationalityKey] = singaporeCitizen;
                
                else this.changeNominatorLocal[nationalityKey] = singaporeCitizen;
            }
        },

        onNomineeShareholderChange(data) {
            this.changeNominatorLocal.isExistingNominator = true;

            const nomineeShareholder = data.originData;
            const nominatorType = nomineeShareholder.shareholder_type.includes('Individual') ? NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL : NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE;

            if(nomineeShareholder) {
            // reset current existing nominator fields
                if(nomineeShareholder?.email?.toLowerCase() === this.changeNominatorLocal.nominator?.email.toLowerCase())
                    this.changeNominatorLocal.nominator = null;

                if(nominatorType)
                    this.reInitializeNominators(nominatorType);

                // filed current nominator data
                if(nomineeShareholder.shareholder) {
                    const nominatorFromShareholder = this.isNominatorInShareholders(nomineeShareholder.shareholder);
                    
                    this.changeNominatorLocal.isExistingNominator = nominatorFromShareholder;
                    this.changeNominatorLocal.nominatorType ={
                        text: nominatorType,
                        value: nominatorType
                    };

                    if(this.changeNominatorLocal.isExistingNominator) {
                        this.changeNominatorLocal.nominator = {
                            userId: nomineeShareholder.shareholder.user_id,
                            name: nomineeShareholder.shareholder.user?.user_info?.legal_name ?? nomineeShareholder.shareholder.user.name,
                            email: nomineeShareholder.shareholder.user.email,
                        };
                    } else {
                        this.changeNominatorLocal.nominatorName = nomineeShareholder.shareholder.user?.user_info?.legal_name ?? nomineeShareholder.shareholder.user.name;
                        this.changeNominatorLocal.nominatorNRIC = nomineeShareholder.shareholder.user?.user_info?.NRIC;
                        this.changeNominatorLocal.nominatorAddress = nomineeShareholder.shareholder.user?.user_info?.address;
                        
                        if(nominatorType === SHAREHOLDER.TYPE.INDIVIDUAL) {
                            this.changeNominatorLocal.nominatorIdType = nomineeShareholder?.user?.user_info?.id_type ? 
                                {
                                    id: nomineeShareholder.shareholder.user.user_info.id_type.id,
                                    name: nomineeShareholder.shareholder.user.user_info.id_type.name,
                                } 
                                : null;
                            this.changeNominatorLocal.nominatorNationality = nomineeShareholder.shareholder?.user?.user_info?.nationality ? 
                                {
                                    value: nomineeShareholder.shareholder.user.user_info.nationality.id,
                                    text: nomineeShareholder.shareholder.user.user_info.nationality.name,
                                } 
                                : null;
                            this.changeNominatorLocal.nominatorDateOfExpiry = formatDate(nomineeShareholder?.shareholder?.user?.user_info?.date_of_expiry);
                            this.changeNominatorLocal.nominatorDOB = formatDate(nomineeShareholder?.shareholder?.user?.user_info?.DOB);
                        }
                    }

                    if(nominatorType === SHAREHOLDER.TYPE.INDIVIDUAL) {
                        this.changeNominatorLocal.nominatorAlias = nomineeShareholder.shareholder.alias;
                    }

                    if(nominatorType === SHAREHOLDER.TYPE.CORPORATE) {
                        this.changeNominatorLocal.nominatorUen = nomineeShareholder?.shareholder?.uen;
                        this.changeNominatorLocal.nominatorLegalForm = nomineeShareholder?.shareholder?.legal_form;
                        this.changeNominatorLocal.nominatorJurisdiction = nomineeShareholder?.shareholder?.jurisdiction ? {
                            id: nomineeShareholder?.shareholder?.jurisdiction.id,
                            name: nomineeShareholder?.shareholder?.jurisdiction.name,
                        } : undefined;
                        this.changeNominatorLocal.nominatorStatue = nomineeShareholder?.shareholder?.statue ? {
                            id: nomineeShareholder?.shareholder?.statue.id,
                            name: nomineeShareholder?.shareholder?.statue.name,
                        } : undefined;
                        this.changeNominatorLocal.nominatorRegistrar = nomineeShareholder?.shareholder?.registrar ? {
                            id: nomineeShareholder?.shareholder?.registrar.id,
                            name: nomineeShareholder?.shareholder?.registrar.name,
                        } : undefined;
                    }
                }
            }
        },

        isNominatorInShareholders(nominator) {
            return this.dataShareholders.filter(shareholder => shareholder?.email?.toLowerCase() === nominator.user?.email?.toLowerCase()).length > 0;
        },

        reInitializeNominators(type)
        {
            if(this.changeNominatorLocal.shareholder?.email) {
                this.nominatorsOptions = this.dataShareholders.filter(shareholder => 
                    shareholder.shareholderType.text === type &&
                    this.changeNominatorLocal.shareholder?.email?.toLowerCase() !== shareholder.email.toLowerCase() 
                ).map(shareholder => ({
                    reference_id: shareholder.referenceId,
                    user_id: shareholder.user_id,
                    name: shareholder.name,
                    email: shareholder.email,
                    nric: shareholder.nric
                }));;
            }
            else {
                this.nominatorsOptions = this.dataShareholders.filter(shareholder => shareholder.shareholderType.text === type).map(shareholder => ({
                    reference_id: shareholder.referenceId,
                    user_id: shareholder.user_id,
                    name: shareholder.name,
                    email: shareholder.email,
                    nric: shareholder.nric
                }));
            }
        },

        async onNominatorTypeChange(payload)
        {
            this.reInitializeNominators(payload.value);
            this.changeNominatorLocal.nominator = undefined;
            this.changeNominatorLocal.nominatorName = '';
            this.changeNominatorLocal.nominatorUen = null;
        },

        onNominatorChange(nominator) {
            if(! this.isExistingNominator) {
                if(this.changeNominatorLocal.nominatorType.text)
                    this.requestAutoFillNominatorFormData(nominator.nric);
            }
        },

        onNominatorJurisdictionChange(payload)
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === payload.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === payload.id);
            this.changeNominatorLocal.nominatorStatue = '';
            this.changeNominatorLocal.nominatorRegistrar = '';
        },

        // eslint-disable-next-line func-names
        requestAutoFillNominatorFormData: debounce(async function(nric) {
            const type = this.changeNominatorLocal?.nominatorType?.text;

            if(type && nric) {
                const nominator = await User.getByNRIC(nric, type);

                if(! nominator) this.changeNominatorLocal.nominatorEmail = undefined;

                if(nominator) this.fillNominatorData(nominator);
            }
        }, 500),
        
        fillNominatorData(nominator) {
            if(! this.changeNominatorLocal.isExistingNominator) {
                this.changeNominatorLocal.nominatorName = nominator.user_info?.legal_name ?? nominator?.name;
                // just make sure backend not generate email for new nominator
                this.changeNominatorLocal.nominatorEmail = nominator?.email;
                this.changeNominatorLocal.nominatorNRIC = nominator.user_info?.NRIC;
                this.changeNominatorLocal.nominatorDateOfExpiry = formatDate(nominator?.user_info?.date_of_expiry);
                this.changeNominatorLocal.nominatorDOB = formatDate(nominator?.user_info?.DOB);

                if (nominator.user_info?.id_type) {
                    this.changeNominatorLocal.nominatorIdType = {
                        id: nominator.user_info.id_type?.id,
                        name: nominator.user_info.id_type?.name
                    };
                }
                if (nominator.user_info?.nationality) {
                    this.changeNominatorLocal.nominatorNationality = {
                        text: nominator.user_info.nationality?.name,
                        value: nominator.user_info.nationality?.id
                    };
                }
                
                this.changeNominatorLocal.nominatorAddress = nominator?.user_info?.address;
            }

            if(this.isNominatorTypeIndividual) 
                this.changeNominatorLocal.nominatorAlias = nominator?.individual_shareholder?.alias;

            if(! this.isNominatorTypeIndividual) {
                this.changeNominatorLocal.nominatorUen = nominator?.user_info?.uen;
                this.changeNominatorLocal.nominatorStatue = nominator?.shareholder?.statue;
                this.changeNominatorLocal.nominatorRegistrar = nominator?.shareholder?.registrar;
                this.changeNominatorLocal.nominatorJurisdiction = nominator?.shareholder?.jurisdiction;
                this.changeNominatorLocal.nominatorLegalForm = nominator?.shareholder?.legal_form;
            }
        },

        generateChangeNominatorForm() {
            this.$refs.refChangeNomineeShareholderForm.validate().then(success => {
                if (success) {
                    this.$emit('generate', JSON.parse(JSON.stringify(this.changeNominatorLocal)));
                }
            });
        },

        clearLocalData() {
            Object.keys(this.changeNominatorLocal).forEach(key => {
                this.changeNominatorLocal[key] = undefined;
            });
        },

        clearFormData() {
            this.clearLocalData();
            this.$refs.refChangeNomineeShareholderForm.reset();
        }
    },
};
  
</script>

<style scope>
.create-or-view-change-shareholder-nominator .preloader {
    width: 15px;
    height: 15px;
    left: 29% !important;
}

.create-or-view-change-shareholder-nominator .preloader div:before {
    background: var(--primary-color);
}

.create-or-view-change-shareholder-nominator .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>


  